import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
} from '@mui/joy';
import QuoteVisualDesignerCard from '../../modules/docs/components/QuoteVisualDesignerCard';
import type { DocumentLineInput } from '../../modules/docs/types/documents.types';
import { documentsService } from '../../modules/docs/services/documents.service';
import {
  isSurveyWritable,
  visualDesignerSurveysService,
  type VisualDesignerSurvey,
} from '../../modules/docs/services/visualDesignerSurveys.service';
import { firstSurveyShowsTransmittedLock } from '../../modules/docs/utils/surveySheetDelete';

export type VersionLockInfo = {
  id: string;
  versionNumber: number;
  isTransmitted: boolean;
  overrideUnlocked: boolean;
  visualSurveyId?: string | null;
};

interface WorkspaceVisualDesignerCardProps {
  projectId: string;
  customerId?: string | null;
  defaultReference?: string;
  /** Required: First/Final survey tab locks the editor to this survey. */
  lockedSurveyId: string;
  /** Bump after unlock/relock so writable state refreshes. */
  lockRefreshKey?: number;
  /** Quote Edit mode — Visual Designer is not independently writable in view. */
  quoteEditing?: boolean;
  /** Final Balance issued — Final is permanently historical. */
  finalHistorical?: boolean;
}

export default function WorkspaceVisualDesignerCard({
  projectId,
  customerId,
  defaultReference,
  lockedSurveyId,
  lockRefreshKey = 0,
  quoteEditing = false,
  finalHistorical = false,
}: WorkspaceVisualDesignerCardProps) {
  const [surveys, setSurveys] = useState<VisualDesignerSurvey[]>([]);
  const [activeSurveyId, setActiveSurveyId] = useState<string>('');
  const [lines, setLines] = useState<DocumentLineInput[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const persistTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const linesRef = useRef<DocumentLineInput[]>([]);
  const skipNextPersist = useRef(true);
  const pendingPersistRef = useRef(false);
  const activeSurveyIdRef = useRef(activeSurveyId);
  const writableRef = useRef(true);

  const activeSurvey = useMemo(
    () => surveys.find((s) => s.id === activeSurveyId) || null,
    [surveys, activeSurveyId]
  );

  const [resolvedLock, setResolvedLock] = useState<VersionLockInfo | null>(null);

  const surveyWritable = isSurveyWritable(activeSurvey, resolvedLock, {
    firstIsTransmitted: !!(activeSurvey?.kind === 'first' && resolvedLock?.isTransmitted),
    unresolvedPendingReview: activeSurvey?.kind === 'final' && activeSurvey.completionStatus === 'complete',
  });
  const writable = quoteEditing && surveyWritable;
  const showFirstTransmissionLock = firstSurveyShowsTransmittedLock({
    surveyKind: activeSurvey?.kind,
    firstVersionIsTransmitted: resolvedLock?.isTransmitted,
  });
  activeSurveyIdRef.current = activeSurveyId;
  writableRef.current = writable;

  useEffect(() => {
    const versionId = activeSurvey?.documentVersionId;
    if (!versionId) {
      setResolvedLock(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const loaded = await documentsService.getVersion(versionId);
        if (cancelled) return;
        const header: any = loaded.header || {};
        setResolvedLock({
          id: String(header.id || versionId),
          versionNumber: Number(header.version_number || 0),
          isTransmitted: !!header.is_transmitted,
          overrideUnlocked: !!header.override_unlocked,
          visualSurveyId: header.visual_survey_id ? String(header.visual_survey_id) : null,
        });
      } catch {
        if (!cancelled) setResolvedLock(null);
      }
    })();
    return () => { cancelled = true; };
  }, [activeSurvey?.documentVersionId, activeSurvey?.id, lockRefreshKey]);

  const loadSurveys = useCallback(async (options?: { showSpinner?: boolean }) => {
    const showSpinner = options?.showSpinner !== false;
    if (showSpinner) setLoading(true);
    setError(null);
    try {
      const list = await visualDesignerSurveysService.listByProject(projectId);
      setSurveys(list);
      setActiveSurveyId((prev) => {
        if (list.some((s) => s.id === lockedSurveyId)) return lockedSurveyId;
        if (prev && list.some((s) => s.id === prev)) return prev;
        return list[0]?.id || '';
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load Visual Designer surveys');
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [projectId, lockedSurveyId]);

  useEffect(() => {
    void loadSurveys();
  }, [loadSurveys, lockRefreshKey]);

  useEffect(() => {
    if (lockedSurveyId && lockedSurveyId !== activeSurveyId) {
      if (surveys.some((s) => s.id === lockedSurveyId)) {
        setActiveSurveyId(lockedSurveyId);
      }
    }
  }, [lockedSurveyId, surveys, activeSurveyId]);

  useEffect(() => {
    const surveyIdForFlush = activeSurveyId;
    if (!activeSurveyId) {
      setLines([]);
      return;
    }
    let cancelled = false;
    skipNextPersist.current = true;
    (async () => {
      try {
        const items = await visualDesignerSurveysService.listItems(activeSurveyId);
        if (cancelled) return;
        linesRef.current = items;
        setLines(items);
        skipNextPersist.current = false;
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load survey items');
      }
    })();
    return () => {
      cancelled = true;
      if (persistTimer.current) {
        clearTimeout(persistTimer.current);
        persistTimer.current = null;
      }
      if (pendingPersistRef.current && surveyIdForFlush && writableRef.current) {
        pendingPersistRef.current = false;
        void visualDesignerSurveysService
          .replaceItemsAndProject(surveyIdForFlush, linesRef.current)
          .catch((err) => {
            console.warn('Failed to flush Visual Designer save:', err);
          });
      }
    };
  }, [activeSurveyId]);

  const persistLines = useCallback(async (next: DocumentLineInput[], surveyId = activeSurveyIdRef.current) => {
    if (!surveyId || !writableRef.current) return;
    setSaving(true);
    setError(null);
    try {
      await visualDesignerSurveysService.replaceItemsAndProject(surveyId, next);
      pendingPersistRef.current = false;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save Visual Designer');
    } finally {
      setSaving(false);
    }
  }, []);

  const flushPendingPersist = useCallback(() => {
    if (persistTimer.current) {
      clearTimeout(persistTimer.current);
      persistTimer.current = null;
    }
    if (!pendingPersistRef.current) return;
    const surveyId = activeSurveyIdRef.current;
    if (!surveyId || !writableRef.current) {
      pendingPersistRef.current = false;
      return;
    }
    void persistLines(linesRef.current, surveyId);
  }, [persistLines]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushPendingPersist();
    };
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!pendingPersistRef.current && !persistTimer.current) return;
      flushPendingPersist();
      event.preventDefault();
      event.returnValue = '';
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('beforeunload', onBeforeUnload);
      flushPendingPersist();
    };
  }, [flushPendingPersist]);

  const handleSetLines: React.Dispatch<React.SetStateAction<DocumentLineInput[]>> = (updater) => {
    setLines((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      linesRef.current = next;
      if (skipNextPersist.current) {
        return next;
      }
      pendingPersistRef.current = true;
      if (persistTimer.current) clearTimeout(persistTimer.current);
      const surveyId = activeSurveyIdRef.current;
      persistTimer.current = setTimeout(() => {
        persistTimer.current = null;
        void persistLines(next, surveyId);
      }, 500);
      return next;
    });
  };

  return (
    <Box sx={{ width: '100%' }}>
      {error && <Alert color="danger" sx={{ mb: 1.25 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size="sm" />
        </Box>
      ) : (
        <>
          <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }} flexWrap="wrap">
            {saving && <Chip size="sm" variant="soft">Saving</Chip>}
            {showFirstTransmissionLock && (
              <Chip size="sm" color="warning" variant="soft">
                Locked with transmitted quote. Updates should be made on the Final Survey
              </Chip>
            )}
          </Stack>

          {!writable && activeSurvey?.kind === 'final' && activeSurvey.completionStatus === 'complete' && (
            <Alert color="warning" sx={{ mb: 1.25 }}>
              {finalHistorical
                ? 'Final Survey is historical. The Final Balance has been issued. Subsequent adjustments use a Variation or Credit invoice.'
                : 'Final Survey is complete. Reopen Final Survey to start another working cycle.'}
            </Alert>
          )}

          {activeSurvey && (
            <QuoteVisualDesignerCard
              hideShell
              readOnly={!writable}
              lines={lines}
              setLines={writable ? handleSetLines : setLines}
              defaultReference={defaultReference}
              customerId={customerId}
              currentVersionId={null}
              availableVersions={[]}
              requireSurveyorMeasures={activeSurvey?.kind === 'final'}
            />
          )}
        </>
      )}
    </Box>
  );
}
